import { Component, Input, forwardRef, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { ApprovalService } from './approval/services/approval.service';
import { ApprovalFlowDefinition } from './approval/models/approval-flow-definition.model';
import { ResolvedApprovalStep } from './approval/models/approval-flow.model';
import { ApprovalFlowPreviewComponent } from './approval-flow-preview/approval-flow-preview.component';
import { environment } from '../../services/global-environment';
import { AuthService, RoleOption } from '../../services/auth.service';
import { DepartmentService } from './approval/services/department.service';
import { UserService } from './approval/services/user.service';
import { Department } from './approval/models/approval-flow-definition.model';
import { I18nService } from '../../services/i18n.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { GuidService } from '../../services/guid.service';
import { convertLegacyStepToMultiApprover } from './approval/utils/approval-flow.utils';

@Component({
  selector: 'app-form-approval-flow',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  template: `
    <div class="form-section approval-flow-section">
      <label [attr.for]="id" [class.required-field]="required">
        <i class="fas fa-route"></i> {{ label }}
      </label>

      <!-- 預覽模式：顯示解析後的簽核流程資訊 -->
      <div *ngIf="preview" class="approval-flow-display">
        <!-- 根據 approvalFlowId 或 value 顯示真實的簽核流程 -->
        <div *ngIf="selectedFlow" class="flow-preview-card">
          <div class="flow-preview-header">
            <i class="fas fa-check-circle flow-icon"></i>
            <span class="flow-name">{{ selectedFlow.name }}</span>
            <span class="flow-status active">{{ 'form.approval_flow.enabled' | translate }}</span>
          </div>

          <div class="flow-preview-content">
            <div class="flow-description" *ngIf="selectedFlow.description">
              {{ selectedFlow.description }}
            </div>

            <!-- 詳細的簽核步驟顯示 -->
            <div class="flow-steps" *ngIf="selectedFlow.steps?.length">
              <div class="steps-label" (click)="toggleStepsExpanded()" [class.expanded]="isStepsExpanded">
                <span>{{ 'form.approval_flow.approval_steps' | translate }}:</span>
                <i class="fas fa-chevron-down toggle-icon" [class.rotated]="isStepsExpanded"></i>
              </div>

              <div class="steps-content" [class.expanded]="isStepsExpanded" *ngIf="isStepsExpanded">
                <div *ngFor="let step of selectedFlow.steps; let i = index" class="step-item">
                  <div class="step-header">
                    <div class="step-number">{{ i + 1 }}</div>
                    <div class="step-name">{{ step.name }}</div>
                    <div class="step-optional" *ngIf="step.optional">{{ 'form.approval_flow.optional' | translate }}</div>
                  </div>

                  <div class="step-details">
                    <!-- ✨ v2.0.0 多簽核者支援 -->
                    <div class="detail-row" *ngIf="step.approvers && step.approvers.length > 0">
                      <span class="label">{{ 'form.approval_flow.approver' | translate }}：</span>
                      <div class="value">
                        <div class="approvers-list">
                          <div *ngFor="let approver of step.approvers; let i = index" class="approver-item">
                            <span class="approver-index">{{ i + 1 }}.</span>
                            <span class="approver-type-badge"
                                  [class.role-based]="approver.type === 'role_based'"
                                  [class.specific-user]="approver.type === 'specific_user'">
                              {{ approver.type === 'role_based' ? ('form.approval_flow.role_based' | translate) : ('form.approval_flow.specific_user' | translate) }}
                            </span>

                            <!-- 角色型簽核者顯示 -->
                            <ng-container *ngIf="approver.type === 'role_based'">
                              <span [class.dynamic-department]="approver.isDynamicDepartment">
                                {{ getDepartmentName(approver.departmentId || '', step) }}
                              </span>
                              <span> - {{ getRoleTranslatedName(approver.roleId || '') }}</span>
                            </ng-container>

                            <!-- 指定人員簽核者顯示 -->
                            <ng-container *ngIf="approver.type === 'specific_user'">
                              <span>{{ approver.userName || getUserName(approver.userId || '') }}</span>
                            </ng-container>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- ✨ 總是顯示可簽核人員列表（合併所有簽核者的人員） -->
                    <div class="detail-row">
                      <span class="label">{{ 'form.approval_flow.eligible_approvers' | translate }}：</span>
                      <div class="value">
                        <!-- 有人員時顯示人員列表 -->
                        <div class="user-list" *ngIf="hasUserList(step)">
                          <span *ngFor="let user of getUserList(step); let last = last" class="user-item">
                            {{ user.name }}
                            <span *ngIf="!last" class="separator">、</span>
                          </span>
                        </div>
                        <!-- 沒有人員時顯示錯誤訊息 -->
                        <div class="user-list-error" *ngIf="!hasUserList(step)">
                          <span class="error-message" style="color: #d9534f; font-weight: bold;">
                            <i class="fas fa-exclamation-triangle" style="margin-right: 4px;"></i>
                            {{ 'form.approval_flow.no_eligible_approvers' | translate }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div class="detail-row">
                      <span class="label">{{ 'form.approval_flow.require_comment' | translate }}：</span>
                      <span class="value">{{ step.requireComment ? ('common.yes' | translate) : ('common.no' | translate) }}</span>
                    </div>

                    <div class="detail-row">
                      <span class="label">{{ 'form.approval_flow.require_signature' | translate }}：</span>
                      <span class="value">{{ step.requireSignature ? ('common.yes' | translate) : ('common.no' | translate) }}</span>
                    </div>

                    <!-- 自訂表單欄位顯示 -->
                    <div class="detail-row" *ngIf="step.customFormItems && step.customFormItems.length > 0">
                      <span class="label">{{ 'form.approval_flow.custom_form_fields' | translate }}：</span>
                      <div class="value">
                        <div class="custom-form-items">
                          <div *ngFor="let item of step.customFormItems; let i = index" class="custom-form-item">
                            <span class="item-index">{{ i + 1 }}.</span>
                            <span class="item-label">{{ item.label }}</span>
                            <span class="item-type">({{ getFormItemTypeDisplay(item.type) }})</span>
                            <span class="required-indicator" *ngIf="item.required">*</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 沒有選擇流程時的顯示 -->
        <div *ngIf="!selectedFlow" class="no-flow-selected">
          <i class="fas fa-info-circle"></i>
          <span>{{ 'form.approval_flow.no_flow_configured' | translate }}</span>
        </div>
      </div>

      <!-- 編輯模式：下拉選單（在屬性設定中使用） -->
      <div *ngIf="!preview">
        <select
          [id]="id"
          [name]="name"
          [ngModel]="value"
          (ngModelChange)="onFlowChange($event)"
          (blur)="onTouched()"
          class="form-input approval-flow-select"
        >
          <option value="" disabled>{{ placeholder }}</option>
          <option *ngFor="let flow of availableFlows" [value]="flow.id">
            {{ flow.name }}
          </option>
        </select>

        <!-- 載入狀態 -->
        <div *ngIf="loading" class="loading-indicator">
          <i class="fas fa-spinner fa-spin"></i>
          <span>{{ 'form.approval_flow.loading' | translate }}</span>
        </div>

        <!-- 錯誤狀態 -->
        <div *ngIf="error" class="error-message">
          <i class="fas fa-exclamation-triangle"></i>
          <span>{{ error }}</span>
        </div>

        <!-- 選中流程的詳細資訊（編輯模式） -->
        <div *ngIf="selectedFlow" class="flow-details">
          <div class="flow-info-header">
            <i class="fas fa-info-circle"></i>
            <span>{{ 'form.approval_flow.flow_details' | translate }}</span>
          </div>

          <div class="flow-info-content">
            <div class="flow-name">{{ selectedFlow.name }}</div>
            <div class="flow-description" *ngIf="selectedFlow.description">
              {{ selectedFlow.description }}
            </div>

            <div class="flow-meta">
              <span class="meta-item">
                <i class="fas fa-list"></i>
                {{ (selectedFlow.steps.length || 0) + ' ' + ('form.approval_flow.approval_steps_count' | translate) }}
              </span>
              <span class="meta-item" *ngIf="selectedFlow.formTypes?.length">
                <i class="fas fa-tags"></i>
                {{ 'form.approval_flow.applicable' | translate }}: {{ selectedFlow.formTypes.join(', ') }}
              </span>
            </div>

            <!-- 簽核步驟預覽 -->
            <div class="steps-preview" *ngIf="selectedFlow.steps?.length">
              <div class="steps-header">{{ 'form.approval_flow.approval_steps' | translate }}:</div>
              <div class="steps-list">
                <div
                  *ngFor="let step of selectedFlow.steps; let i = index"
                  class="step-item"
                >
                  <span class="step-number">{{ step.order }}</span>
                  <span class="step-name">{{ step.name }}</span>
                  <span class="step-approver">
                    <!-- ✨ v2.0.0 支援新格式（多簽核者） -->
                    <ng-container *ngIf="step.approvers && step.approvers.length > 0">
                      <span *ngFor="let approver of step.approvers; let last = last">
                        <ng-container *ngIf="approver.type === 'role_based'">
                          ({{ approver.roleName || ('form.approval_flow.role' | translate) }})
                        </ng-container>
                        <ng-container *ngIf="approver.type === 'specific_user'">
                          ({{ approver.userName || ('form.approval_flow.specific_user' | translate) }})
                        </ng-container>
                        <span *ngIf="!last">、</span>
                      </span>
                    </ng-container>

                    <!-- 向下相容：支援舊格式（單簽核者） -->
                    <ng-container *ngIf="!step.approvers && step.approverType">
                      <ng-container *ngIf="step.approverType === 'role_based'">
                        ({{ step.roleName || ('form.approval_flow.role' | translate) }})
                      </ng-container>
                      <ng-container *ngIf="step.approverType === 'specific_user'">
                        ({{ step.userName || ('form.approval_flow.specific_user' | translate) }})
                      </ng-container>
                    </ng-container>
                  </span>
                </div>
         </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormApprovalFlowComponent),
      multi: true
    }
  ],
  styleUrls: ['total-component.scss']
})
export class FormApprovalFlowComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() label: string = '';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() placeholder: string = '';
  @Input() required: boolean = false;
  @Input() preview: boolean = false; // 新增：是否為預覽模式
  @Input() approvalFlowId?: string; // 新增：指定的簽核流程 ID (可選)
  @Input() getuserlist: boolean = false; // 新增：是否在簽核元件中顯示人員列表
  @Input() initiatorInfo: any; // 新增：填寫者資訊


  value: string = '';
  availableFlows: ApprovalFlowDefinition[] = [];
  selectedFlow: ApprovalFlowDefinition | null = null;
  loading = false;
  error: string | null = null;

  // 新增：資料來源
  departments: Department[] = [];

  // 新增：收合控制
  isStepsExpanded: boolean = false;

  // ControlValueAccessor callback 函數
  onChange = (value: any) => {};
  onTouched = () => {};

  constructor(
    private approvalService: ApprovalService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private departmentService: DepartmentService,
    private userService: UserService,
    private http: HttpClient,
    private i18nService: I18nService,
    private guidService: GuidService
  ) {}

  ngOnInit(): void {
    // 設定預設值（如果沒有提供）
    if (!this.label) {
      this.label = this.i18nService.translate('form.approval_flow.title');
    }
    if (!this.placeholder) {
      this.placeholder = this.i18nService.translate('form.approval_flow.placeholder');
    }

    this.loadDepartments();
    this.loadApprovalFlows();
    console.log("initiatorInfo", this.initiatorInfo);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // 當 approvalFlowId 改變時，更新選中的流程
    if (changes['approvalFlowId'] && this.approvalFlowId && this.availableFlows.length > 0) {
      this.updateSelectedFlowById(this.approvalFlowId);
    }
  }


  loadApprovalFlows(): void {
    this.loading = true;
    this.error = null;

    const assistantNo = this.router.routerState.snapshot.root.queryParams['id'];

    if (!assistantNo) {
      this.error = this.i18nService.translate('form.approval_flow.missing_company_id_or_assistant');
      this.loading = false;
      return;
    }

    this.approvalService.getApprovalFlowDefinitions(assistantNo).subscribe({
      next: (response) => {

        console.log('Approval flows loaded:', response);

        if (response && response.success && response.data) {
          this.availableFlows = response.data.filter((flow: ApprovalFlowDefinition) => flow.status === 'active');
          console.log('Available flows after filtering:', this.availableFlows);

          // 手動觸發變更檢測
          this.cdr.detectChanges();
        } else {
          this.availableFlows = [];
          console.log('No data received or response format incorrect');
        }
        this.loading = false;

        // 優先使用 approvalFlowId（來自父元件的設定）
        if (this.approvalFlowId) {
          this.updateSelectedFlowById(this.approvalFlowId);
          // 如果指定了 approvalFlowId，直接查詢該流程的詳細資訊
          console.log('Loading specific approval flow:', this.approvalFlowId);
          this.loadSpecificApprovalFlow(this.approvalFlowId);
        } else if (this.value) {
          // 如果沒有 approvalFlowId，則使用表單值
          this.updateSelectedFlow(this.value);
        }
      },
      error: (err: any) => {
        this.error = this.i18nService.translate('form.approval_flow.load_error');
        console.error('Error loading approval flows', err);
        this.loading = false;
      }
    });
  }

  private updateSelectedFlowById(flowId: string): void {
    this.selectedFlow = this.availableFlows.find(flow => flow.id === flowId) || null;
    // 在預覽模式下，不需要更新表單值
    if (!this.preview) {
      this.value = flowId;
    }
  }

  onFlowChange(flowId: string): void {
    this.value = flowId;
    this.updateSelectedFlow(flowId);

    // 輸出完整的簽核流程資訊，而不只是 flowId
    const fullApprovalData = this.generateFullApprovalData();
    this.onChange(fullApprovalData);
  }

  private updateSelectedFlow(flowId: string): void {
    this.selectedFlow = this.availableFlows.find(flow => flow.id === flowId) || null;
  }

  // ControlValueAccessor 介面實作
  writeValue(value: any): void {
    this.value = value || '';
    if (this.value && this.availableFlows.length > 0) {
      this.updateSelectedFlow(this.value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // 可以在這裡實作禁用狀態的邏輯
  }

  // 新增方法：判斷值是否為解析後的簽核流程資料
  isResolvedFlowData(value: any): value is ResolvedApprovalStep[] {
    return Array.isArray(value) && value.length > 0 &&
           value[0] && typeof value[0] === 'object' &&
           'level' in value[0] && 'stepName' in value[0] && 'approverName' in value[0];
  }

  // 新增方法：載入特定的簽核流程資訊
  private loadSpecificApprovalFlow(flowId: string): void {
    console.log('載入特定簽核流程:', flowId);

    const companyid = environment.companyid;
    const assistantNo = this.router.routerState.snapshot.root.queryParams['id'];

    if (!companyid || !assistantNo) {
      console.error(this.i18nService.translate('form.approval_flow.missing_required_params'));
      return;
    }

    this.approvalService.getApprovalFlowDefinitionById(flowId, assistantNo).subscribe({
      next: (flowDef) => {
        if (flowDef) {
          console.log('載入的簽核流程定義:', flowDef);
          this.selectedFlow = flowDef;
          // 載入完成後，預載入所有指定使用者的名稱
          // this.preloadUserNames();

          // 總是載入人員列表（簡化邏輯）
          console.log('載入人員列表 - preview:', this.preview, ', getuserlist:', this.getuserlist);
          this.loadApprovalUserLists();
          this.cdr.detectChanges();
        } else {
          console.warn(this.i18nService.translate('form.approval_flow.flow_not_found', { flowId }));
        }
      },
      error: (error) => {
        console.error(this.i18nService.translate('form.approval_flow.load_flow_definition_error'), error);
      }
    });
  }

  // 新增方法：載入部門資料
  loadDepartments(): void {
    this.departmentService.getAllDepartments().subscribe({
      next: (departments) => {
        this.departments = departments;
        console.log('Departments loaded successfully:', departments.length, 'departments');
      },
      error: (err) => {
        console.error('Error loading departments', err);
      }
    });
  }

  // 取得角色選項
  get roleOptions(): RoleOption[] {
    return this.authService.roleOptions;
  }

  // 取得部門名稱
  getDepartmentName(departmentId: string, step?: any): string {
    if (!departmentId) return '';

    // 處理動態部門
    if (departmentId === 'submitter_dept') {
      // 總是顯示實際部門名稱
      const actualName = step?.actualDepartmentName || '';
      return actualName ? this.i18nService.translate('form.approval_flow.same_as_submitter_dept_with_name', { actualName }) : this.i18nService.translate('form.approval_flow.same_as_submitter_dept');
    }
    if (departmentId === 'parent_dept') {
      // 總是顯示實際部門名稱
      const actualName = step?.actualDepartmentName || '';
      return actualName ? this.i18nService.translate('form.approval_flow.submitter_parent_dept_with_name', { actualName }) : this.i18nService.translate('form.approval_flow.submitter_parent_dept');
    }

    // 處理固定部門
    const dept = this.departments.find(d => d.id === departmentId);
    return dept?.name || this.i18nService.translate('form.approval_flow.unknown_department', { departmentId });
  }

  // 取得角色的翻譯名稱（支援多國語系）
  getRoleTranslatedName(roleId: string): string {
    if (!roleId) return '';

    // 先檢查是否為基礎角色，如果是則使用翻譯
    const roleOption = this.roleOptions.find(option => option.value === roleId);
    if (roleOption) {
      return roleOption.label;
    }

    // 如果不是基礎角色，則使用原本的名稱
    return this.i18nService.translate('form.approval_flow.unknown_role', { roleId });
  }

  // 新增方法：切換步驟展開/收合
  toggleStepsExpanded(): void {
    this.isStepsExpanded = !this.isStepsExpanded;
  }

  // 新增方法：取得使用者名稱
  getUserName(userId: string): string {
    if (!userId) return this.i18nService.translate('form.approval_flow.unspecified');

    // 檢查是否已經有 userName
    if (this.selectedFlow && this.selectedFlow.steps) {
      const step = this.selectedFlow.steps.find(s => s.userId === userId);
      if (step && step.userName) {
        return step.userName;
      }
    }

    // 如果沒有 userName，且還沒有在載入中，才發送請求
    if (!this.isLoadingUserName(userId)) {
      this.loadUserName(userId);
    }

    return userId; // 預設回傳 userId
  }

  // 追蹤正在載入的使用者名稱
  private loadingUserNames = new Set<string>();

  // 檢查是否正在載入特定使用者名稱
  private isLoadingUserName(userId: string): boolean {
    return this.loadingUserNames.has(userId);
  }

  // 載入使用者名稱（只載入一次）
  private loadUserName(userId: string): void {
    if (this.loadingUserNames.has(userId)) {
      return; // 已在載入中，不重複載入
    }

    this.loadingUserNames.add(userId);

    this.userService.getUsernameById(userId).subscribe({
      next: (username) => {
        this.loadingUserNames.delete(userId);
        if (username) {
          // 找到對應的步驟並更新其 userName 屬性
          if (this.selectedFlow && this.selectedFlow.steps) {
            const step = this.selectedFlow.steps.find(s => s.userId === userId);
            if (step) {
              step.userName = username;
              this.cdr.detectChanges(); // 觸發變更檢測
            }
          }
        }
      },
      error: (error) => {
        this.loadingUserNames.delete(userId);
        console.error(this.i18nService.translate('form.approval_flow.get_username_error'), error);
      }
    });
  }

  // 輔助方法：檢查步驟是否有使用者列表
  hasUserList(step: any): boolean {
    return step && step.userlist && Array.isArray(step.userlist) && step.userlist.length > 0;
  }

  // 輔助方法：取得步驟的使用者列表
  getUserList(step: any): any[] {
    return (step && step.userlist && Array.isArray(step.userlist)) ? step.userlist : [];
  }

  // 預載入所有指定人員的使用者名稱
  preloadUserNames(): void {
    if (!this.selectedFlow || !this.selectedFlow.steps) {
      return;
    }

    // 總是載入簽核人員列表
    console.log(this.i18nService.translate('form.approval_flow.getting_user_list'));
    this.loadApprovalUserLists();
  }

  // 新增方法：載入簽核流程對應的人員列表（支援多簽核者 v2.0.0）
  private loadApprovalUserLists(): void {
    console.log('=== 開始載入簽核人員列表（v2.0.0 多簽核者支援）===');
    console.log('選中的流程:', this.selectedFlow);
    console.log('填寫者資訊:', this.initiatorInfo);

    if (!this.selectedFlow || !this.selectedFlow.steps) {
      console.warn('沒有選中的流程或流程步驟');
      return;
    }

    // 遍歷每個步驟，為每個步驟建立 userlist 陣列
    this.selectedFlow.steps.forEach((step, index) => {
      console.log(`\n--- 處理步驟 ${index + 1}: ${step.name} ---`);

      // ✨ 確保步驟有 approvers 陣列（向下相容轉換）
      const convertedStep = convertLegacyStepToMultiApprover(step);
      console.log('轉換後的步驟:', convertedStep);
      console.log('簽核者數量:', convertedStep.approvers.length);

      // 初始化 userlist 陣列
      if (!step.hasOwnProperty('userlist')) {
        (step as any).userlist = [];
      }

      // ✨ 遍歷所有簽核者
      if (convertedStep.approvers && convertedStep.approvers.length > 0) {
        this.loadApproversForStep(step, convertedStep.approvers, index);
      } else {
        console.warn(`步驟 ${index + 1} 沒有簽核者配置`);
        (step as any).userlist = [];
      }
    });

    console.log('=== 簽核人員列表載入程序完成 ===');
  }

  // ✨ 新增方法：載入步驟的所有簽核者
  private loadApproversForStep(step: any, approvers: any[], stepIndex: number): void {
    const allUserLists: any[][] = [];
    let completedCount = 0;

    console.log(`步驟 ${stepIndex + 1} 開始載入 ${approvers.length} 個簽核者`);

    approvers.forEach((approver, approverIndex) => {
      console.log(`\n處理簽核者 ${approverIndex + 1}:`, approver);

      if (approver.type === 'specific_user' && approver.userId) {
        console.log(`簽核者 ${approverIndex + 1} - 指定人員 (userId: ${approver.userId})`);
        this.userService.getUsernameById(approver.userId).subscribe({
          next: (username) => {
            console.log(`簽核者 ${approverIndex + 1} - 取得指定人員名稱: ${username}`);
            if (username) {
              allUserLists[approverIndex] = [{
                id: approver.userId,
                name: username
              }];
            } else {
              allUserLists[approverIndex] = [];
              console.warn(`找不到使用者ID為 ${approver.userId} 的人員名稱`);
            }

            completedCount++;
            if (completedCount === approvers.length) {
              this.mergeUserLists(step, allUserLists);
            }
          },
          error: (error) => {
            console.error(`取得指定人員錯誤 (userId: ${approver.userId}):`, error);
            allUserLists[approverIndex] = [];
            completedCount++;
            if (completedCount === approvers.length) {
              this.mergeUserLists(step, allUserLists);
            }
          }
        });
      } else if (approver.type === 'role_based' && approver.departmentId && approver.roleId) {
        console.log(`簽核者 ${approverIndex + 1} - 部門角色 (dept: ${approver.departmentId}, role: ${approver.roleId})`);
        this.loadRoleBasedApprover(step, approver, allUserLists, approverIndex, approvers.length, () => {
          completedCount++;
          if (completedCount === approvers.length) {
            this.mergeUserLists(step, allUserLists);
          }
        });
      } else {
        console.warn(`簽核者 ${approverIndex + 1} 配置不完整:`, approver);
        allUserLists[approverIndex] = [];
        completedCount++;
        if (completedCount === approvers.length) {
          this.mergeUserLists(step, allUserLists);
        }
      }
    });
  }

  // ✨ 新增方法：載入角色簽核者
  private loadRoleBasedApprover(
    step: any,
    approver: any,
    allUserLists: any[][],
    approverIndex: number,
    totalCount: number,
    onComplete: () => void
  ): void {
    // 處理動態部門 ID
    let actualDepartmentId = approver.departmentId;

    if (approver.departmentId === 'submitter_dept') {
      // 取得填寫者的部門 ID
      actualDepartmentId = this.initiatorInfo?.departmentId || '';
      console.log(`動態部門 - 填寫者部門: ${actualDepartmentId}`);

      // 同時取得實際部門名稱
      if (actualDepartmentId) {
        const dept = this.departments.find(d => d.id === actualDepartmentId);
        (step as any).actualDepartmentName = dept?.name || '';
        console.log(`實際部門名稱: ${(step as any).actualDepartmentName}`);
      } else {
        console.warn('填寫者資訊中沒有部門ID');
      }
    } else if (approver.departmentId === 'parent_dept') {
      console.log('處理上級部門...');
      // 取得填寫者的上級部門 ID（異步操作）
      this.departmentService.getParentDepartmentId(this.initiatorInfo?.departmentId || '').subscribe({
        next: (parentDeptId) => {
          console.log(`取得上級部門ID: ${parentDeptId}`);
          if (parentDeptId && approver.roleId) {
            // 取得實際部門名稱
            const dept = this.departments.find(d => d.id === parentDeptId);
            (step as any).actualDepartmentName = dept?.name || '';

            this.getDepartmentRoleUsers(parentDeptId, approver.roleId).subscribe({
              next: (users: any[]) => {
                console.log(`上級部門找到 ${users.length} 個人員:`, users);
                allUserLists[approverIndex] = users.map((user: any) => ({
                  id: user.id,
                  name: user.username || user.name
                }));
                onComplete();
              },
              error: (error: any) => {
                console.error(`取得上級部門角色人員失敗 (parentDeptId: ${parentDeptId}, roleId: ${approver.roleId}):`, error);
                allUserLists[approverIndex] = [];
                onComplete();
              }
            });
          } else {
            console.warn(`上級部門資訊不完整 - parentDeptId: ${parentDeptId}, roleId: ${approver.roleId}`);
            allUserLists[approverIndex] = [];
            onComplete();
          }
        },
        error: (error) => {
          console.error('取得上級部門ID失敗:', error);
          allUserLists[approverIndex] = [];
          onComplete();
        }
      });
      return; // 因為是異步操作，直接返回
    }

    // 部門+角色：透過 API 取得該部門該角色的所有人員
    if (actualDepartmentId && approver.roleId) {
      console.log(`查詢部門角色人員 - 部門: ${actualDepartmentId}, 角色: ${approver.roleId}`);
      this.getDepartmentRoleUsers(actualDepartmentId, approver.roleId).subscribe({
        next: (users: any[]) => {
          console.log(`找到 ${users.length} 個符合條件的人員:`, users);
          allUserLists[approverIndex] = users.map((user: any) => ({
            id: user.id,
            name: user.username || user.name
          }));
          onComplete();
        },
        error: (error: any) => {
          console.error(`取得部門角色人員失敗 (departmentId: ${actualDepartmentId}, roleId: ${approver.roleId}):`, error);
          allUserLists[approverIndex] = [];
          onComplete();
        }
      });
    } else {
      console.warn(`部門角色資訊不完整 - departmentId: ${actualDepartmentId}, roleId: ${approver.roleId}`);
      allUserLists[approverIndex] = [];
      onComplete();
    }
  }

  // ✨ 新增方法：合併所有簽核者的使用者列表
  private mergeUserLists(step: any, allUserLists: any[][]): void {
    console.log('\n🔀 === 開始合併人員列表 ===');
    console.log('收到的所有人員列表:', allUserLists);
    console.log('列表數量:', allUserLists.length);

    // 合併所有簽核者的使用者列表（去除重複）
    const mergedUsers: any[] = [];
    const userIds = new Set<string>();

    allUserLists.forEach((userList, index) => {
      console.log(`處理列表 ${index}:`, userList);
      if (userList && Array.isArray(userList)) {
        userList.forEach((user) => {
          if (!userIds.has(user.id)) {
            userIds.add(user.id);
            mergedUsers.push(user);
            console.log(`✅ 新增人員: ${user.name} (${user.id})`);
          } else {
            console.log(`⏭️  跳過重複人員: ${user.name} (${user.id})`);
          }
        });
      }
    });

    (step as any).userlist = mergedUsers;
    console.log('✅ 合併完成！');
    console.log('合併後的人員列表:', mergedUsers);
    console.log('總人數:', mergedUsers.length);
    console.log('步驟物件的 userlist:', step.userlist);

    // 觸發變更檢測
    this.cdr.detectChanges();

    // 更新完整資料並通知父元件
    this.updateFullApprovalData();
    console.log('🔀 === 合併人員列表完成 ===\n');
  }


  // 新增方法：取得特定部門特定角色的使用者列表
  private getDepartmentRoleUsers(departmentId: string, roleId: string): Observable<any[]> {
    const companyid = environment.companyid;
    const assistantNo = this.router.routerState.snapshot.root.queryParams['id'];

    console.log('=== 開始查詢部門角色人員 ===');
    console.log('部門ID:', departmentId);
    console.log('角色ID:', roleId);
    console.log('公司ID:', companyid);
    console.log('助手編號:', assistantNo);
    console.log('填寫者資訊:', this.initiatorInfo);

    if (!companyid || !assistantNo) {
      console.warn('缺少必要參數: companyid 或 assistantNo');
      return of([]);
    }

    // 確保參數正確編碼
    const url = `${environment.apiUrl}/api/users/department-role-users`;
    const params = {
      companyid: companyid,
      assistantNo: assistantNo || 'ALL',   // 如果沒有 assistantNo，使用 ALL
      departmentid: departmentId,
      roleid: roleId,
      onlyCurrentDepartment: 'true'  // 只回傳該部門符合角色的人員
    };

    console.log('API 請求參數:', params);
    console.log('完整 API URL:', `${url}?${new URLSearchParams(params).toString()}`);

    return this.http.get<{
      success: boolean;
      data?: any[];
      error?: string;
      message?: string;
    }>(url, { params }).pipe(
      map((response: any) => {
        console.log('=== API 回應結果 ===');
        console.log('完整回應:', response);
        
        if (response && response.success && response.data && Array.isArray(response.data)) {
          console.log(`找到 ${response.data.length} 個符合條件的人員:`, response.data);
          return response.data;
        } else {
          console.warn('API 回應異常或沒有資料:');
          console.warn('- success:', response?.success);
          console.warn('- data:', response?.data);
          console.warn('- error:', response?.error);
          console.warn('- message:', response?.message);
          return [];
        }
      }),
      catchError((error: any) => {
        console.error('=== API 請求失敗 ===');
        console.error('錯誤詳情:', error);
        console.error('狀態碼:', error?.status);
        console.error('錯誤訊息:', error?.message);
        console.error('完整錯誤物件:', error);
        return of([]);
      })
    );
  }

  // ✨ v2.0.0 產生完整的簽核流程資料（支援多簽核者）
  private generateFullApprovalData(): any {
    console.log('\n📋 === 開始產生完整簽核流程資料 ===');

    if (!this.selectedFlow || !this.selectedFlow.steps) {
      console.warn('沒有選中的流程或步驟');
      return '';
    }

    console.log('原始流程:', this.selectedFlow);

    const approvalSteps = this.selectedFlow.steps.map((step, index) => {
      console.log(`\n處理步驟 ${index + 1}: ${step.name}`);

      // ✨ 確保步驟有 approvers 陣列（向下相容轉換）
      const convertedStep = convertLegacyStepToMultiApprover(step);
      console.log('轉換後的步驟:', convertedStep);

      // 基礎步驟資料
      const stepData: any = {
        stepDefinitionId: this.guidService.generateGuid(),
        level: index + 1,
        stepName: step.name,
        requireComment: step.requireComment || false,
        requireSignature: step.requireSignature || false,
        optional: step.optional || false
      };

      // ✨ 新增：多簽核者陣列
      if (convertedStep.approvers && convertedStep.approvers.length > 0) {
        stepData.approvers = convertedStep.approvers.map((approver: any) => {
          const approverData: any = {
            type: approver.type
          };

          if (approver.type === 'specific_user') {
            approverData.userId = approver.userId;
            approverData.userName = approver.userName || this.getUserName(approver.userId || '');
          } else if (approver.type === 'role_based') {
            approverData.departmentId = approver.departmentId;
            approverData.roleId = approver.roleId;
            approverData.departmentName = approver.departmentName || this.getDepartmentName(approver.departmentId || '', step);
            approverData.roleName = approver.roleName || this.getRoleTranslatedName(approver.roleId || '');
            approverData.isDynamicDepartment = approver.isDynamicDepartment || false;
          }

          console.log('簽核者資料:', approverData);
          return approverData;
        });

        // ✨ 新增：簽核邏輯
        stepData.approvalLogic = convertedStep.approvalLogic || 'any_one';
        console.log('簽核邏輯:', stepData.approvalLogic);
      }

      // ✨ 加入合併後的可簽核人員列表
      if ((step as any).userlist && Array.isArray((step as any).userlist)) {
        stepData.approverList = (step as any).userlist;
        console.log('可簽核人員列表:', stepData.approverList);
      } else {
        console.warn('步驟沒有 userlist 或格式不正確');
      }

      // 處理自訂表單欄位
      if (step.customFormItems && step.customFormItems.length > 0) {
        stepData.customFormItems = step.customFormItems;
        console.log('自訂表單欄位:', stepData.customFormItems);
      }

      console.log('完整步驟資料:', stepData);
      return stepData;
    });

    // 返回完整的簽核流程資料結構
    const fullData = {
      type: 'approval-flow',
      value: {
        flowId: this.selectedFlow.id,
        flowName: this.selectedFlow.name,
        flowDescription: this.selectedFlow.description,
        steps: approvalSteps
      }
    };

    console.log('✅ 完整簽核流程資料:', fullData);
    console.log('📋 === 產生完整簽核流程資料完成 ===\n');

    return fullData;
  }

  // 新增方法：更新完整簽核資料並通知父元件
  private updateFullApprovalData(): void {
    const fullApprovalData = this.generateFullApprovalData();
    this.onChange(fullApprovalData);
    this.cdr.detectChanges();
  }

  // 新增方法：取得表單元件類型的顯示名稱
  getFormItemTypeDisplay(type: string): string {
    const typeDisplayMap: { [key: string]: string } = {
      'text-input': this.i18nService.translate('form.builder.text_input'),
      'textarea': this.i18nService.translate('form.builder.textarea'),
      'number-input': this.i18nService.translate('form.builder.number_input'),
      'date': this.i18nService.translate('form.builder.date'),
      'datetime': this.i18nService.translate('form.builder.datetime'),
      'select': this.i18nService.translate('form.builder.select'),
      'radiobutton': this.i18nService.translate('form.builder.radiobutton'),
      'multi-checkbox': this.i18nService.translate('form.builder.multi_checkbox'),
      'file-upload': this.i18nService.translate('form.builder.file_upload'),
      'signature': this.i18nService.translate('form.builder.signature')
    };

    return typeDisplayMap[type] || type;
  }
}
